import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Typography
} from '@mui/material';
import api from '../../../services/api';

const ACTION_TYPES = [
  { value: 'assign_persona', label: 'Assign Persona' },
  { value: 'human_review', label: 'Human Review Required' },
  { value: 'escalate', label: 'Escalate' },
  { value: 'alternative_pathway', label: 'Alternative Pathway' }
];

const ROLES = [
  { value: 'clinical_supervisor', label: 'Clinical Supervisor' },
  { value: 'therapist', label: 'Therapist' },
  { value: 'admin', label: 'Admin' }
];

const ActionBuilder = ({ action, onChange, personas: propPersonas }) => {
  const [personas, setPersonas] = useState(propPersonas || []);

  useEffect(() => {
    // Fetch personas if not passed as prop
    if (!propPersonas || propPersonas.length === 0) {
      api.get('/personas').then(res => {
        // API returns array directly, not { personas: [...] }
        const personaList = Array.isArray(res.data) ? res.data : (res.data.personas || []);
        setPersonas(personaList);
      }).catch(err => {
        console.error('Failed to load personas:', err);
      });
    }
  }, [propPersonas]);

  const handleFieldChange = (field, value) => {
    onChange({ ...action, [field]: value });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
      <Typography variant="subtitle2" gutterBottom>
        Action Configuration
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Action Type</InputLabel>
        <Select
          value={action?.type || 'assign_persona'}
          onChange={(e) => handleFieldChange('type', e.target.value)}
          label="Action Type"
          size="small"
        >
          {ACTION_TYPES.map(type => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {action?.type === 'assign_persona' && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Persona</InputLabel>
          <Select
            value={action?.personaKey || ''}
            onChange={(e) => handleFieldChange('personaKey', e.target.value)}
            label="Persona"
            size="small"
            required
            error={!action?.personaKey}
          >
            {personas.map(persona => (
              <MenuItem key={persona.key} value={persona.key}>
                {persona.name} ({persona.key})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {['human_review', 'escalate'].includes(action?.type) && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Assign To Role</InputLabel>
          <Select
            value={action?.assignToRole || ''}
            onChange={(e) => handleFieldChange('assignToRole', e.target.value)}
            label="Assign To Role"
            size="small"
            required
          >
            {ROLES.map(role => (
              <MenuItem key={role.value} value={role.value}>
                {role.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {action?.type === 'alternative_pathway' && (
        <TextField
          fullWidth
          type="number"
          label="Route to Stage"
          value={action?.routeToStage || 1}
          onChange={(e) => handleFieldChange('routeToStage', Number(e.target.value))}
          helperText="Stage number to route to"
          sx={{ mb: 2 }}
          size="small"
          required
        />
      )}

      <TextField
        fullWidth
        multiline
        rows={2}
        label="Reason / Message *"
        value={action?.reason || ''}
        onChange={(e) => handleFieldChange('reason', e.target.value)}
        helperText="Required: Explanation for this action (shown in triage review)"
        size="small"
        required
        error={!action?.reason}
      />
    </Paper>
  );
};

export default ActionBuilder;
